import { commonHeader, endpoints } from "~/config/apiConfig";
import type {
  KintoneLoginCredentials,
  PaginatedReportsResponse,
  ReverseEngineeringDocument,
  ReverseEngineeringReportDetail,
  StartAnalyzeBody,
} from "~/interface/reverse";

export class ReverseApi {
  private getRequestInfo(method: string, requestBody?: object): RequestInit {
    const requestInit: RequestInit = {
      method: method,
      headers: commonHeader,
      credentials: "include",
      keepalive: true,
    };

    if (requestBody !== undefined) {
      requestInit.body = JSON.stringify(requestBody);
    }

    return requestInit;
  }

  async getReverseEngineeringReports(
    organization_id: string,
    project_id: string,
    page: number = 1,
    limit: number = 10,
  ): Promise<PaginatedReportsResponse | null> {
    const baseEndpoint = endpoints.getReverseEngineeringReports(organization_id, project_id);
    const endpoint = `${baseEndpoint}?page=${page}&limit=${limit}`;
    const requestInfo = this.getRequestInfo("GET");

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        const data = await response.json();
        return data;
      } else if (response.status === 404) {
        return {
          reports: [],
          totalCount: 0,
          page,
          limit,
        };
      } else {
        const errorMessage = await response.json();
        console.error(errorMessage.message);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async getReverseEngineeringReport(
    organization_id: string,
    project_id: string,
    report_id: string,
  ): Promise<ReverseEngineeringReportDetail | null> {
    const endpoint = endpoints.getReverseEngineeringReport(organization_id, project_id, report_id);
    const requestInfo = this.getRequestInfo("GET");

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        const data = await response.json();
        // APIが文字列を返す場合は、JSONとしてパースする
        if (typeof data === "string") {
          try {
            return JSON.parse(data);
          } catch (parseError) {
            console.error("Failed to parse response:", parseError);
            return null;
          }
        }
        return data;
      } else if (response.status === 404) {
        return null;
      } else {
        const errorMessage = await response.json();
        console.error(errorMessage.message);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async getKintoneAppsList(
    organization_id: string,
    project_id: string,
    credentials: KintoneLoginCredentials,
  ): Promise<any | null> {
    const endpoint = endpoints.getKintoneAppsList(organization_id, project_id);
    const requestInfo = this.getRequestInfo("POST", credentials);

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        const data = await response.json();
        // df_instance_idとアプリ一覧が返ってくる
        return data;
      } else {
        const errorMessage = await response.json();
        console.error(errorMessage.message);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async prepareReverseEngineering(
    organization_id: string,
    project_id: string,
    body: any,
  ): Promise<
    { reportId: string; instanceId: string } | { error: "CREDIT_LACK"; lackCredit: number } | null
  > {
    const endpoint = endpoints.prepareReverseEngineering(organization_id, project_id);
    const requestInfo = this.getRequestInfo("POST", body);

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        const data = await response.json();

        // df_instance_idを返す
        const result = {
          reportId: data.report_id || data.reportId || data.id,
          instanceId: data.df_instance_id || data.instance_id || data.instanceId,
        };

        return result;
      } else {
        const errorMessage = await response.json();
        console.error(errorMessage.message);

        // Check for credit lack error
        if (
          response.status === 402 ||
          errorMessage.code === "CREDIT_LACK" ||
          errorMessage.error === "CREDIT_LACK"
        ) {
          return {
            error: "CREDIT_LACK",
            lackCredit: errorMessage.lack_credit || errorMessage.lackCredit || 0,
          };
        }

        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async startAnalyzeReverseEngineering(
    organization_id: string,
    project_id: string,
    report_id: string,
    body: StartAnalyzeBody,
  ): Promise<boolean | { error: "CREDIT_LACK"; lackCredit: number }> {
    const endpoint = endpoints.startAnalyzeReverseEngineering(organization_id, project_id);
    const requestInfo = this.getRequestInfo("POST", {
      ...body,
      report_id: report_id,
    });

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return true;
      } else {
        const errorMessage = await response.json();
        console.error(errorMessage.message);

        // Check for credit lack error
        if (
          response.status === 402 ||
          errorMessage.code === "CREDIT_LACK" ||
          errorMessage.error === "CREDIT_LACK"
        ) {
          return {
            error: "CREDIT_LACK",
            lackCredit: errorMessage.lack_credit || errorMessage.lackCredit || 0,
          };
        }

        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async deleteReverseEngineeringReport(
    organization_id: string,
    project_id: string,
    report_id: string,
  ): Promise<boolean> {
    const endpoint = endpoints.deleteReverseEngineeringReport(
      organization_id,
      project_id,
      report_id,
    );
    const requestInfo = this.getRequestInfo("DELETE");

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return true;
      } else {
        const errorMessage = await response.json();
        console.error(errorMessage.message);
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async getInstanceStatus(
    organization_id: string,
    project_id: string,
    df_instance_id: string,
  ): Promise<any | null> {
    const endpoint = endpoints.getInstanceStatus(organization_id, project_id, df_instance_id);
    const requestInfo = this.getRequestInfo("GET");

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        const errorMessage = await response.json();
        console.error(errorMessage.message);
        // 404エラーの場合はエラー情報をthrow
        if (errorMessage.code === "INFRASTRUCTURE_NOT_FOUND" || errorMessage.statusCode === 404) {
          throw errorMessage;
        }
        return null;
      }
    } catch (error) {
      console.error(error);
      // エラー情報をそのままthrow
      throw error;
    }
  }

  async getReverseEngineeringDocuments(
    organization_id: string,
    project_id: string,
    parent_report_id: string,
  ): Promise<ReverseEngineeringDocument[] | null> {
    const endpoint = endpoints.getReverseEngineeringDocuments(
      organization_id,
      project_id,
      parent_report_id,
    );
    const requestInfo = this.getRequestInfo("GET");

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        const documents = await response.json();
        return documents;
      } else if (response.status === 404) {
        return [];
      } else {
        const errorMessage = await response.json();
        console.error(errorMessage.message);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async updateReverseEngineeringDocument(
    organization_id: string,
    project_id: string,
    document_id: string,
    content: string,
  ): Promise<boolean> {
    const endpoint = endpoints.updateReverseEngineeringDocument(
      organization_id,
      project_id,
      document_id,
    );
    const requestInfo = this.getRequestInfo("POST", { content });

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return true;
      } else {
        const errorMessage = await response.json();
        console.error(errorMessage.message);
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async updateReverseEngineeringReportName(
    organization_id: string,
    project_id: string,
    report_id: string,
    name: string,
  ): Promise<boolean> {
    const endpoint = endpoints.updateReverseEngineeringReportName(
      organization_id,
      project_id,
      report_id,
    );
    const requestInfo = this.getRequestInfo("POST", { name });

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return true;
      } else {
        const errorMessage = await response.json();
        console.error(errorMessage.message);
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async createProcessLock(
    organization_id: string,
    project_id: string,
    document_id: string,
    mode: string,
  ): Promise<boolean> {
    const endpoint = endpoints.createReverseEngineeringProcessLock(
      organization_id,
      project_id,
      document_id,
    );
    const requestInfo = this.getRequestInfo("POST", { mode });

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return true;
      } else {
        const errorMessage = await response.json();
        console.error(errorMessage.message);
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async deleteProcessLock(
    organization_id: string,
    project_id: string,
    document_id: string,
    mode: string,
  ): Promise<boolean> {
    const endpoint = endpoints.deleteReverseEngineeringProcessLock(
      organization_id,
      project_id,
      document_id,
    );
    const requestInfo = this.getRequestInfo("DELETE", { mode });

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return true;
      } else {
        const errorMessage = await response.json();
        console.error(errorMessage.message);
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }
}
